import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  CButton, CCard, CCardBody, CCardHeader, CCardTitle, CCol, CListGroup,
  CListGroupItem, CRow, CSpinner,
} from '@coreui/react-pro'

import { api } from '../api/client'
import { usePersonLabels, joinLabels } from '../api/people'
import StatusBadge from '../components/StatusBadge'
import Linkify from '../components/Linkify'
import { fmtDate, fmtTime, mapsSearchUrl, tabRef } from '../components/format'

interface Basic { key: string; value: string; label: string }
interface Flight {
  id: number; traveler_ids: number[]; direction: 'arrival' | 'departure'
  date: string; time_local: string; airport: string; flight_number: string
}
interface Transport {
  id: number; member_ids: number[]; rental: boolean; spare_seats: number | null
  pickup: string; status: string
}
interface Stay {
  id: number; traveler_ids: number[]; hotel: string; town: string
  check_in: string; check_out: string; status: 'booked' | 'planning'
}

// One bullet of a basics value: "Google Maps: 'X'" links to the map search,
// "see People tab" links to that page, anything else linkifies URLs/domains.
const BulletText: React.FC<{ text: string }> = ({ text }) => {
  const maps = mapsSearchUrl(text)
  if (maps) {
    return (
      <a href={maps} target="_blank" rel="noopener noreferrer">{text}</a>
    )
  }
  const tab = tabRef(text)
  if (tab) {
    return <>{tab.lead}<Link to={tab.route}>{tab.label}</Link></>
  }
  return <Linkify text={text} />
}

// Basics values are stored as " · "-separated text; show them as bullets.
const BulletedValue: React.FC<{ value: string }> = ({ value }) => {
  const items = value.split('·').map((s) => s.trim()).filter(Boolean)
  if (items.length < 2) return <BulletText text={value} />
  return (
    <ul className="mb-0 ps-3">
      {items.map((item, i) => (
        <li key={i}><BulletText text={item} /></li>
      ))}
    </ul>
  )
}

const Dashboard: React.FC = () => {
  const basics = useQuery({
    queryKey: ['basics'],
    queryFn: () => api.get<{ items: Basic[] }>('/v1/basics'),
  })
  const flights = useQuery({
    queryKey: ['flights'],
    queryFn: () => api.get<Flight[]>('/v1/flights'),
  })
  const transport = useQuery({
    queryKey: ['transport'],
    queryFn: () => api.get<Transport[]>('/v1/transport'),
  })
  const stays = useQuery({
    queryKey: ['stays'],
    queryFn: () => api.get<Stay[]>('/v1/stays'),
  })
  const calendar = useQuery({
    queryKey: ['calendar-url'],
    queryFn: () => api.get<{ url: string }>('/v1/calendar-url'),
    retry: false,
  })
  const labels = usePersonLabels()
  const [copied, setCopied] = useState(false)

  const arrivals = (flights.data ?? [])
    .filter((f) => f.direction === 'arrival')
    .sort((a, b) => (a.date + a.time_local).localeCompare(b.date + b.time_local))
  const departures = (flights.data ?? [])
    .filter((f) => f.direction === 'departure')
    .sort((a, b) => (a.date + b.time_local).localeCompare(b.date + b.time_local))

  const rentals = (transport.data ?? []).filter((t) => t.rental)
  // An arrival is covered when some rental car's group shares a traveler with it.
  const needRide = arrivals.filter(
    (f) => !rentals.some((t) => (t.member_ids ?? []).some((id) => f.traveler_ids.includes(id)))
  )

  // Pre/post-trip extensions, chronological — who's somewhere else, and when.
  const extensions = (stays.data ?? [])
    .slice()
    .sort((a, b) => (a.check_in + a.check_out).localeCompare(b.check_in + b.check_out))
  const nights = (s: Stay) =>
    Math.round((new Date(s.check_out).getTime() - new Date(s.check_in).getTime()) / 86_400_000)

  const copyCalendar = async () => {
    const url = calendar.data?.url
    if (!url) return
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      window.prompt('Copy this URL:', url)
    }
  }

  if (basics.isLoading) {
    return <div className="text-center py-5"><CSpinner /></div>
  }

  return (
    <>
      <h3 className="mb-3">🌴 Costa Rica 2027</h3>

      {/* Trip basics */}
      <CRow className="mb-4">
        {['dates', 'home_base', 'checkin_checkout', 'travelers'].map((k) => {
          const item = (basics.data?.items ?? []).find((b) => b.key === k)
          if (!item) return null
          return (
            <CCol sm={6} lg={3} key={k}>
              <CCard className="h-100">
                <CCardBody>
                  <div className="small text-uppercase text-body-secondary">{item.label}</div>
                  <div className="mt-1">
                    <BulletedValue value={item.value} />
                  </div>
                </CCardBody>
              </CCard>
            </CCol>
          )
        })}
      </CRow>

      {/* Calendar subscribe */}
      <CCard className="mb-4 border-primary">
        <CCardBody className="d-flex flex-column flex-sm-row align-items-sm-center gap-3">
          <div className="flex-grow-1">
            <div className="fw-semibold">📅 Trip calendar</div>
            <div className="small text-body-secondary">
              Subscribe once — flights and booked activities appear on your phone
              automatically as they're added. In Google Calendar: Other calendars → From URL.
            </div>
          </div>
          {calendar.data?.url ? (
            <CButton color="primary" onClick={copyCalendar}>
              {copied ? '✓ Copied' : 'Copy calendar link'}
            </CButton>
          ) : null}
        </CCardBody>
      </CCard>

      <CRow className="mb-4">
        {/* Arrivals */}
        <CCol lg={6} className="mb-4">
          <CCard className="h-100">
            <CCardHeader><CCardTitle className="mb-0">🛬 Arrivals in Costa Rica</CCardTitle></CCardHeader>
            <CListGroup flush>
              {arrivals.length === 0 && (
                <CListGroupItem className="text-body-secondary">
                  No flights logged yet — add yours under ✈️ Flights.
                </CListGroupItem>
              )}
              {arrivals.map((f) => (
                <CListGroupItem key={f.id} className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="fw-semibold">{joinLabels(labels, f.traveler_ids) || 'The group'}</div>
                    <div className="small text-body-secondary">
                      {fmtDate(f.date)} · {fmtTime(f.time_local)} · {f.airport} {f.flight_number}
                    </div>
                  </div>
                  <StatusBadge status="arrival" />
                </CListGroupItem>
              ))}
            </CListGroup>
          </CCard>
        </CCol>

        {/* Departures */}
        <CCol lg={6} className="mb-4">
          <CCard className="h-100">
            <CCardHeader><CCardTitle className="mb-0">🛫 Departures from Costa Rica</CCardTitle></CCardHeader>
            <CListGroup flush>
              {departures.length === 0 && (
                <CListGroupItem className="text-body-secondary">Nothing logged yet.</CListGroupItem>
              )}
              {departures.map((f) => (
                <CListGroupItem key={f.id} className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="fw-semibold">{joinLabels(labels, f.traveler_ids) || 'The group'}</div>
                    <div className="small text-body-secondary">
                      {fmtDate(f.date)} · {fmtTime(f.time_local)} · {f.airport} {f.flight_number}
                    </div>
                  </div>
                  <StatusBadge status="departure" />
                </CListGroupItem>
              ))}
            </CListGroup>
          </CCard>
        </CCol>
      </CRow>

      {/* Trip extensions — pre/post-trip hotels outside the villa week */}
      {extensions.length > 0 && (
        <CCard className="mb-4">
          <CCardHeader><CCardTitle className="mb-0">🏨 Trip extensions</CCardTitle></CCardHeader>
          <CListGroup flush>
            {extensions.map((s) => (
              <CListGroupItem key={s.id} className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="fw-semibold">
                    {joinLabels(labels, s.traveler_ids) || 'The group'}
                  </div>
                  <div className="small text-body-secondary">
                    {[s.hotel, s.town].filter(Boolean).join(' · ') || 'Somewhere TBD'} — {' '}
                    {fmtDate(s.check_in)} → {fmtDate(s.check_out)} · {nights(s)} night{nights(s) === 1 ? '' : 's'}
                  </div>
                </div>
                <StatusBadge status={s.status} />
              </CListGroupItem>
            ))}
          </CListGroup>
        </CCard>
      )}

      {/* Carpool board */}
      <CRow>
        <CCol lg={6} className="mb-4">
          <CCard className="h-100">
            <CCardHeader><CCardTitle className="mb-0">🚗 Spare seats</CCardTitle></CCardHeader>
            <CListGroup flush>
              {rentals.length === 0 && (
                <CListGroupItem className="text-body-secondary">
                  No rental cars logged yet.
                </CListGroupItem>
              )}
              {rentals.map((t) => (
                <CListGroupItem key={t.id}>
                  <div className="d-flex justify-content-between">
                    <span className="fw-semibold">{joinLabels(labels, t.member_ids)}</span>
                    <span>{t.spare_seats ?? 0} seat{(t.spare_seats ?? 0) === 1 ? '' : 's'}</span>
                  </div>
                  {t.pickup && <div className="small text-body-secondary">Pickup: {t.pickup}</div>}
                  <div className="mt-1"><StatusBadge status={t.status} /></div>
                </CListGroupItem>
              ))}
            </CListGroup>
          </CCard>
        </CCol>
        <CCol lg={6} className="mb-4">
          <CCard className="h-100">
            <CCardHeader>
              <CCardTitle className="mb-0">🤝 Likely need a ride</CCardTitle>
            </CCardHeader>
            <CListGroup flush>
              {needRide.length === 0 && (
                <CListGroupItem className="text-body-secondary">
                  Everyone arriving has a car listed (or no arrivals logged).
                </CListGroupItem>
              )}
              {needRide.map((f) => (
                <CListGroupItem key={f.id}>
                  <span className="fw-semibold">{joinLabels(labels, f.traveler_ids) || 'The group'}</span>
                  <span className="text-body-secondary">
                    {' '}— lands {fmtDate(f.date)} at {fmtTime(f.time_local)} ({f.airport})
                  </span>
                </CListGroupItem>
              ))}
            </CListGroup>
          </CCard>
        </CCol>
      </CRow>
    </>
  )
}

export default Dashboard
